/**
 * useNewVersionBanner — Detects when a new deployment is live and prompts
 * the user to reload for the fresh assets.
 *
 * Strategy: on mount we grab the hash of the current `main.<hash>.js`
 * script tag that CRA emits in the built HTML. Every N minutes (and
 * whenever the tab regains focus) we re-fetch `/index.html` with cache
 * disabled and extract the current main-bundle hash. If it differs from
 * the initial one, we know a redeploy happened — show the banner.
 *
 * Notes:
 *  - CRA only hashes bundle names in production builds. In `yarn start`
 *    dev mode there is no hash, so `getInitialHash()` returns null and
 *    this hook becomes a no-op. That's the desired behaviour.
 *  - We store dismissal in-memory only (per session). We deliberately
 *    do NOT persist a dismissal — the point is to nudge the user, so on
 *    the next tab load if they haven't reloaded, we'll still catch it
 *    because the initial hash will now match the deployed one (having
 *    finally reloaded).
 */
import { useEffect, useState } from 'react';

const CHECK_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes
const MAIN_BUNDLE_RE = /\/static\/js\/main\.([a-z0-9]+)\.js/i;

const getInitialHash = () => {
  if (typeof document === 'undefined') return null;
  const scripts = Array.from(document.querySelectorAll('script[src]'));
  for (const s of scripts) {
    const m = s.src.match(MAIN_BUNDLE_RE);
    if (m) return m[1];
  }
  return null;
};

const fetchDeployedHash = async () => {
  try {
    // Cache-bust with a timestamp param + no-store to always hit the origin.
    const res = await fetch(`/index.html?_v=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!res.ok) return null;
    const html = await res.text();
    const m = html.match(MAIN_BUNDLE_RE);
    return m ? m[1] : null;
  } catch {
    return null;
  }
};

export const useNewVersionBanner = () => {
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const initial = getInitialHash();
    if (!initial) return; // dev mode or missing hash → skip

    let stopped = false;

    const check = async () => {
      if (stopped || dismissed || hasNewVersion) return;
      const deployed = await fetchDeployedHash();
      if (!deployed) return;
      if (deployed !== initial) {
        setHasNewVersion(true);
      }
    };

    // Immediate check after mount (deliberately deferred so first paint
    // isn't blocked by a network round-trip).
    const boot = setTimeout(check, 5000);
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      stopped = true;
      clearTimeout(boot);
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [dismissed, hasNewVersion]);

  const reload = () => {
    // Force a full reload; the `true` arg is legacy but harmless. The
    // ?_r cache-buster query below is what actually forces the browser
    // to skip its HTTP cache for the top-level document.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('_r', Date.now().toString());
      window.location.replace(url.toString());
    } catch {
      window.location.reload();
    }
  };

  const dismiss = () => setDismissed(true);

  return { hasNewVersion, reload, dismiss };
};

export default useNewVersionBanner;
